import { alpha, Box, Button, CircularProgress, styled, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { getPortfolioChartPartialList } from 'store/slices/investment/investmentSlice';
import useDispatch from 'hooks/useDispatch';

const InvestmentLineChart = ({ investmentDetail }) => {
  const dispatch = useDispatch();
  const theme = useTheme()

  const [portfolioChart, setPortfolioChart] = useState([])
  const [loading, setLoading] = useState(true)
  const [series, setSeries] = useState([
    {
      name: 'Main',
      data: [],
      color: '#3498db',
      show: true
    },
    {
      name: 'BTC',
      data: [],
      color: '#F7931A',
    },
    {
      name: 'Gold',
      data: [],
      color: '#f1c40f',
    },
    {
      name: 'ETH',
      data: [],
      color: '#8aceff',
    },
  ]);

  const options = {
    chart: {
      id: 'line-chart',
      toolbar: {
        show: false,
      },
      background: 'transparent',
    },
    labels: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      theme: "dark"
    },
    colors: series.map(line => line.color),
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 3,
    },
    xaxis: {
      categories: portfolioChart?.map((chart) => chart?.dateUnitPrice),
      labels: {
        show: false,
      },
    },
    yaxis: {
      opposite: true,
      labels: {
        formatter: value => value?.toFixed(2),
        style: {
          colors: theme.palette.text.main,
        },
      },
    },
    grid: {
      borderColor: '#34495e',
      strokeDashArray: 3,
    },
    legend: {
      show: false,
    },
  }

  const toggleSeries = async (seriesIndex) => {
    const updatedSeries = series.map((line, index) => {
      if (index === seriesIndex) {
        return { ...line, show: !line.show };
      }
      return line;
    });

    setSeries(updatedSeries);

    const toggled = updatedSeries[seriesIndex];

    if (toggled.show) {
      const percentage = await handleFetchPortfolio(toggled?.name)
      setSeries((prev) =>
        prev.map((line) =>
          line.name === toggled.name ? { ...line, data: percentage } : line
        )
      );
    }
  };

  const handleFetchPortfolio = async (coinName) => {
    try {
      setLoading(true)
      const today = new Date()
      const lastWeek = new Date()
      lastWeek.setDate(today.getDate() - 7)

      const body = {
        id: investmentDetail?.objPortfolio?.id,
        coin: coinName,
        startDate: lastWeek.toISOString().split("T")[0],
        endDate: today.toISOString().split("T")[0]
      }

      const { payload } = await dispatch(getPortfolioChartPartialList(body))

      const chartData = payload?.data?.data?.list

      const seriesPercentage = chartData?.map((chart) => chart?.percentage)

      setPortfolioChart(chartData)
      return seriesPercentage
    } finally {
      setLoading(false)
    }

  }

  const addDataToChart = ({ coinName, data }) => {

    setSeries(prevSeries =>
      prevSeries.map(line =>
        line.name === coinName
          ? { ...line, data }
          : line
      )
    )
  }

  useEffect(() => {

    const handleDefaultSeries = async () => {
      const coinName = series?.find((item) => item.show === true)?.name

      const percentage = await handleFetchPortfolio(coinName)
      addDataToChart({ coinName, data: percentage })
    }

    handleDefaultSeries()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {series.map((line, index) => (
        <Button
          size="small"
          key={index}
          sx={{ backgroundColor: line.show && line.color, margin: '5px' }}
          onClick={() => toggleSeries(index)}
        >
          {line.name}
        </Button >
      ))}

      <Box position={"relative"}>
        {loading &&
          <LoadingWrapper>
            <CircularProgress size={20} color='secondary' />
          </LoadingWrapper>
        }

        <Chart options={options} series={series.filter(line => line.show)} type="area" height={200} />
      </Box>
    </>
  );
};


const LoadingWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  height: "100%",
  backdropFilter: "blur(5px)",
  zIndex: 1,
  background: alpha(theme.palette.secondary.main, 0.1),
  borderRadius: "16px"
}))


export default InvestmentLineChart;